// server-only

import { CardDefinition, ContentType, TextNumber } from '@/types';
import DATA from '@/data';

import { URL_S3 } from '@/helpers/constants';
import { LanguageOptions, ServiceType } from '@/types';

const FRONT_PAGE_ARTICLES = ['1', '2', '3', '4', '5', '6'];

// Reshape the data given
function contentDefinitionMapper(
  item: any,
  serviceType: ServiceType,
  contentType: ContentType,
  contentUrl: TextNumber
) {
  return {
    id: item.id,
    name: item.title,
    timestamp: new Date().getTime(),
    imageSrc: `${URL_S3}/${serviceType}/images/thumbnails/${contentType}/${item.id}.webp`,
    type: contentType,
    tags: item.tags,
    link: `/${contentUrl}/${item.id}`,
  } as CardDefinition;
}

// Use data json and filter then reshape the data (articles, recipes, guides...)
export const getTextData = (
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentType,
  contentUrl: TextNumber,
  tag?: string
) => {
  // Check if data exist for this serviceType and language
  const serviceData = DATA[serviceType]?.[selectedLanguage];
  if (!serviceData || !serviceData[contentType]) {
    throw new Error('Service data or texts data not found');
  }

  // Return the text data for this serviceType and language based on the config file
  return serviceData[contentType]
    .filter((item) => !tag || item.tags.includes(tag))
    .map((item) =>
      contentDefinitionMapper(item, serviceType, contentType, contentUrl)
    );
};

// Find the text content data from data json and then reshape it (article, recipe, guide...)
export const getText = (
  id: string,
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentType,
  contentUrl: TextNumber
) => {
  const serviceData = DATA[serviceType]?.[selectedLanguage];
  if (!serviceData || !serviceData[contentType]) {
    throw new Error('Service data or text data not found');
  }

  const text = serviceData[contentType].find((item) => String(item.id) === id);
  if (text) {
    return contentDefinitionMapper(text, serviceType, contentType, contentUrl);
  }

  throw new Error(`Text with ID ${id} not found`);
};

export const getFrontPageTexts = (
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentType,
  contentUrl: TextNumber
) => {
  const serviceData = DATA[serviceType]?.[selectedLanguage];

  if (!serviceData || !serviceData[contentType]) {
    throw new Error('Service data or texts not found');
  }

  return serviceData[contentType]
    .filter((item) => FRONT_PAGE_ARTICLES.includes(String(item.id)))
    .map((item) =>
      contentDefinitionMapper(item, serviceType, contentType, contentUrl)
    );
};

const mixServices = (
  ...servicesContent: CardDefinition[][]
): CardDefinition[] => {
  const result: CardDefinition[] = [];
  const maxLength = Math.max(
    0,
    ...servicesContent.map((serviceContent) => serviceContent.length)
  );

  for (let i = 0; i < maxLength; i++) {
    servicesContent.forEach((serviceContent) => {
      if (serviceContent[i]) {
        result.push(serviceContent[i]);
      }
    });
  }

  return result;
};

const getMultiServiceTexts = (
  services: ServiceType[],
  selectedLanguage: LanguageOptions,
  contentType: ContentType,
  contentUrl: TextNumber,
  filterIds?: string[]
) => {
  const servicesContent = services.map((serviceType) => {
    const serviceData = DATA[serviceType]?.[selectedLanguage];

    if (!serviceData || !serviceData[contentType]) {
      return [];
    }

    return serviceData[contentType]
      .filter(
        (item) => !filterIds || filterIds.includes(String(item.id))
      )
      .map((item) => ({
        ...contentDefinitionMapper(item, serviceType, contentType, contentUrl),
        serviceType,
      }));
  });

  return mixServices(...servicesContent);
};

export const getFrontPageMultiTexts = (
  services: ServiceType[],
  selectedLanguage: LanguageOptions,
  contentType: ContentType,
  contentUrl: TextNumber
) =>
  getMultiServiceTexts(
    services,
    selectedLanguage,
    contentType,
    contentUrl,
    FRONT_PAGE_ARTICLES
  ).slice(0, FRONT_PAGE_ARTICLES.length);

export const getAllMultiTexts = (
  services: ServiceType[],
  selectedLanguage: LanguageOptions,
  contentType: ContentType,
  contentUrl: TextNumber
) => getMultiServiceTexts(services, selectedLanguage, contentType, contentUrl);

// Get the text content from a S3 file
export const getTextS3Content = async (
  id: string,
  serviceType: ServiceType,
  selectedLanguage: LanguageOptions,
  contentType: ContentType,
  contentUrl: TextNumber
) => {
  let fileContents = '';
  let data = null;

  try {
    data = getText(id, serviceType, selectedLanguage, contentType, contentUrl);
  } catch (error) {
    console.error(
      '[Service Text] Error fetching text data from config:',
      error
    );
  }

  const s3Url = `${URL_S3}/${serviceType}/${contentType}/${selectedLanguage}/${id}.md`;

  try {
    const response = await fetch(s3Url);
    if (!response.ok) {
      throw new Error(`[Service Text] HTTP error status: ${response.status}`);
    }

    fileContents = await response.text();
  } catch (error) {
    console.error('[Service Text] Error fetching text content from S3:', error);
  }

  return {
    id, // id from the param
    data, // data reshape from json
    fileContents, // content from S3
  };
};
